import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAdminReleaseNotes,
  saveReleaseNote,
  archiveReleaseNote,
  restoreReleaseNote,
  getArchivedReleaseNotes,
} from "../../../actions";
import { brand, notice } from "../../../theme/businessFlowsTheme";
import ConfirmDialog from "./ui/ConfirmDialog";
import ArchivedList from "./ui/ArchivedList";
import ViewToggle from "./ui/ViewToggle";

const CHANGE_TYPES = ["NEW", "IMPROVEMENT", "FIX", "DEPRECATED"];

// The same four tones the published page uses, so a note reads the same here
// as it will once it is out.
const TYPE_COLORS = {
  NEW:         { bg: brand.mark,      color: brand.ink,      line: brand.signalDeep },
  IMPROVEMENT: { bg: notice.quiet.bg, color: notice.quiet.fg, line: notice.quiet.line },
  FIX:         { bg: notice.good.bg,  color: notice.good.fg,  line: notice.good.line },
  DEPRECATED:  { bg: notice.fix.bg,   color: notice.fix.fg,   line: notice.fix.line },
};

const emptyForm = () => ({
  version: "",
  title: "",
  date: new Date().toISOString().split("T")[0],
  summary: "",
  changes: [{ type: "NEW", text: "" }],
  published: false,
});

const ReleaseNotesUpdate = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editingVersion, setEditingVersion] = useState(null); // null = create mode
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [view, setView] = useState("active");
  const [archived, setArchived] = useState({ status: "idle", items: [] });
  const [restoringVersion, setRestoringVersion] = useState(null);

  const fetchNotes = useCallback(() => {
    setLoading(true);
    getAdminReleaseNotes().then((data) => {
      setNotes(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  }, []);

  const loadArchived = useCallback(async () => {
    setArchived((prev) => ({ ...prev, status: "loading" }));
    const res = await getArchivedReleaseNotes();
    setArchived(res.success ? { status: "ready", items: res.content || [] } : { status: "error", items: [] });
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { loadArchived(); }, [loadArchived]);

  const handleRestore = async (note) => {
    setRestoringVersion(note.version);
    const res = await restoreReleaseNote(note.version);
    setRestoringVersion(null);
    if (!res.success) {
      toast.error(res.msg || "Couldn't restore the release note. Try again.");
      return;
    }
    toast.success(`v${note.version} restored`);
    fetchNotes();
    loadArchived();
  };

  const openCreate = () => {
    setForm(emptyForm());
    setEditingVersion(null);
    setShowForm(true);
  };

  const openEdit = (note) => {
    setForm({
      version: note.version || "",
      title: note.title || "",
      date: note.date ? note.date.split("T")[0] : new Date().toISOString().split("T")[0],
      summary: note.summary || "",
      changes: note.changes && note.changes.length > 0
        ? note.changes.map((c) => ({ type: c.type || "NEW", text: c.text || "" }))
        : [{ type: "NEW", text: "" }],
      published: Boolean(note.published),
    });
    setEditingVersion(note.version);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingVersion(null);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeItem = (idx, field, value) => {
    setForm((prev) => {
      const changes = [...prev.changes];
      changes[idx] = { ...changes[idx], [field]: value };
      return { ...prev, changes };
    });
  };

  const addChangeItem = () => {
    setForm((prev) => ({
      ...prev,
      changes: [...prev.changes, { type: "NEW", text: "" }],
    }));
  };

  const removeChangeItem = (idx) => {
    setForm((prev) => ({
      ...prev,
      changes: prev.changes.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async (publish) => {
    if (!form.version.trim()) { toast.error("Enter a version, like 3.0.0."); return; }
    if (!form.title.trim())   { toast.error("Enter a title for this release."); return; }
    if (!form.date)            { toast.error("Choose the release date."); return; }

    const cleanChanges = form.changes.filter((c) => c.text.trim() !== "");

    setSaving(true);
    const result = await saveReleaseNote({
      version: form.version.trim(),
      title: form.title.trim(),
      date: form.date,
      summary: form.summary.trim(),
      changes: cleanChanges,
      published: publish !== undefined ? publish : form.published,
    });
    setSaving(false);

    if (result.success) {
      toast.success("Release note saved.");
      fetchNotes();
      cancelForm();
    } else {
      toast.error(result.msg || "Couldn't save the release note. Try again.");
    }
  };

  const handleArchive = async (version) => {
    setDeleting(version);
    const result = await archiveReleaseNote(version);
    setDeleting(null);
    setConfirmDelete(null);
    if (result.success) {
      toast.success(`v${version} archived. Restore it any time from Archived.`);
      fetchNotes();
      loadArchived();
    } else {
      toast.error(result.msg || "Couldn't archive the release note. Try again.");
    }
  };

  const togglePublish = async (note) => {
    const result = await saveReleaseNote({ ...note, published: !note.published });
    if (result.success) {
      toast.success(note.published ? "Unpublished." : "Published.");
      fetchNotes();
    } else {
      toast.error(result.msg || "Couldn't update the release note. Try again.");
    }
  };

  // ─── Form panel ────────────────────────────────────────────────────────────
  // Called as a function, never rendered as <FormPanel />: a component defined
  // inside this one would be a new type on every render, so React would remount
  // the form on each keystroke and the field being typed in would lose focus.
  const renderFormPanel = () => (
    <div style={{
      background: "#fff", border: `1px solid ${brand.line}`, borderRadius: 14,
      padding: "1.5rem", marginBottom: "1.5rem",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
          {editingVersion ? `Edit v${editingVersion}` : "New release note"}
        </h2>
        <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={cancelForm}>Cancel</button>
      </div>

      <div className="row g-3">
        {/* Version */}
        <div className="col-md-3">
          <label className="adm-label">Version *</label>
          <input
            className="adm-input"
            placeholder="e.g. 2.4.1"
            value={form.version}
            disabled={!!editingVersion}
            onChange={(e) => handleFormChange("version", e.target.value)}
          />
          {editingVersion && (
            <div className="adm-help" style={{ marginTop: 4 }}>A version names the release for good, so it can't be changed.</div>
          )}
        </div>

        {/* Date */}
        <div className="col-md-3">
          <label className="adm-label">Date *</label>
          <input
            type="date"
            className="adm-input"
            value={form.date}
            onChange={(e) => handleFormChange("date", e.target.value)}
          />
        </div>

        {/* Title */}
        <div className="col-md-6">
          <label className="adm-label">Title *</label>
          <input
            className="adm-input"
            placeholder="e.g. Performance & UX Improvements"
            value={form.title}
            onChange={(e) => handleFormChange("title", e.target.value)}
          />
        </div>

        {/* Summary */}
        <div className="col-12">
          <label className="adm-label">Summary</label>
          <textarea
            className="adm-input"
            style={{ height: "auto", padding: "8px 12px", resize: "vertical" }}
            rows={2}
            placeholder="Brief description of this release…"
            value={form.summary}
            onChange={(e) => handleFormChange("summary", e.target.value)}
          />
        </div>

        {/* Changes */}
        <div className="col-12">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <label className="adm-label">Changes</label>
            <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={addChangeItem}>
              Add change
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {form.changes.map((change, idx) => (
              <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select
                  className="adm-input"
                  aria-label={`Kind of change ${idx + 1}`}
                  style={{ maxWidth: 150 }}
                  value={change.type}
                  onChange={(e) => handleChangeItem(idx, "type", e.target.value)}
                >
                  {CHANGE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input
                  className="adm-input"
                  placeholder="Describe the change…"
                  value={change.text}
                  onChange={(e) => handleChangeItem(idx, "text", e.target.value)}
                />
                <button
                  className="adm-btn adm-btn--quiet-danger adm-btn--sm"
                  style={{ flexShrink: 0 }}
                  disabled={form.changes.length === 1}
                  onClick={() => removeChangeItem(idx)}
                  title="Remove this change"
                  aria-label={`Remove change ${idx + 1}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="col-12">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", paddingTop: 4 }}>
            <button
              className="adm-btn adm-btn--primary adm-btn--sm"
              disabled={saving}
              onClick={() => handleSave(true)}
            >
              {saving ? "Publishing…" : "Save and publish"}
            </button>
            <button
              className="adm-btn adm-btn--secondary adm-btn--sm"
              disabled={saving}
              onClick={() => handleSave(false)}
            >
              Save as draft
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Main render ────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 900, padding: "0.5rem 0" }}>
      <ToastContainer position="top-right" autoClose={4000} />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={`Archive v${confirmDelete || ""}?`}
        confirmLabel="Archive release note"
        busyLabel="Archiving…"
        busy={deleting === confirmDelete}
        onConfirm={() => handleArchive(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      >
        It disappears from the public release notes. Nothing is deleted: restore it from Archived and it comes
        back as it was.
      </ConfirmDialog>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontWeight: 700, fontSize: "1.25rem", letterSpacing: "-0.02em" }}>Release notes</h1>
          <p style={{ margin: "2px 0 0", fontSize: "0.875rem", color: brand.muted }}>
            What users read under What's new. A draft stays here until you publish it.
          </p>
        </div>
        {!showForm && (
          <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreate}>
            New release note
          </button>
        )}
      </div>

      <div className="adm-toolbar">
        <ViewToggle value={view} onChange={setView} archivedCount={archived.items.length} />
      </div>

      {view === "archived" ? (
        <ArchivedList
          status={archived.status}
          items={archived.items}
          getKey={(n) => n.version}
          renderName={(n) => `v${n.version}${n.title ? ` — ${n.title}` : ""}`}
          renderDetail={(n) => (n.published ? "Was published" : "Was a draft")}
          restoringKey={restoringVersion}
          onRestore={handleRestore}
          onRetry={loadArchived}
          emptyTitle="No archived release notes"
          emptyText="Release notes you archive appear here and can be restored as they were."
        />
      ) : (
      <>
      {showForm && renderFormPanel()}

      {/* Notes list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem 0", color: brand.muted }}>
          <div className="spinner-border spinner-border-sm" role="status" />
          <p style={{ marginTop: 8, fontSize: "0.88rem" }}>Loading…</p>
        </div>
      ) : notes.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "3rem 1rem",
          background: brand.shell, borderRadius: 14, border: `1px dashed ${brand.line}`,
        }}>
          <p style={{ margin: 0, fontWeight: 650 }}>No release notes yet</p>
          <p style={{ color: brand.muted, margin: "4px 0 0", fontSize: "0.875rem" }}>
            Choose New release note, write up what shipped, then publish it.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {notes.map((note) => (
            <div key={note.version} style={{
              background: "#fff", border: `1px solid ${brand.line}`, borderRadius: 14,
              padding: "1rem 1.25rem",
              opacity: note.published ? 1 : 0.8,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                {/* Left: version + title + date */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{
                      fontSize: "0.78rem", fontWeight: 700,
                      background: brand.shell, color: brand.inkSoft,
                      border: `1px solid ${brand.hairline}`,
                      padding: "2px 10px", borderRadius: 999,
                    }}>v{note.version}</span>

                    <span style={{
                      fontSize: "0.7rem", fontWeight: 600, padding: "2px 9px", borderRadius: 999,
                      background: note.published ? notice.good.bg : notice.quiet.bg,
                      color: note.published ? notice.good.fg : notice.quiet.fg,
                      border: `1px solid ${note.published ? notice.good.line : notice.quiet.line}`,
                    }}>
                      {note.published ? "Published" : "Draft"}
                    </span>

                    <span style={{ fontSize: "0.875rem", fontWeight: 600, color: brand.ink }}>{note.title}</span>
                    <span style={{ fontSize: "0.78rem", color: brand.muted, marginLeft: "auto" }}>
                      {note.date ? new Date(note.date).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }) : ""}
                    </span>
                  </div>

                  {note.summary && (
                    <p style={{ margin: "0 0 8px", fontSize: "0.875rem", lineHeight: 1.6, color: brand.prose }}>{note.summary}</p>
                  )}

                  {note.changes && note.changes.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {note.changes.map((c, i) => {
                        const meta = TYPE_COLORS[c.type] || TYPE_COLORS["NEW"];
                        return (
                          <span key={i} style={{
                            fontSize: "0.74rem", padding: "1px 8px", borderRadius: 999,
                            background: meta.bg, color: meta.color, border: `1px solid ${meta.line}`,
                          }}>
                            {c.type}: {c.text.length > 48 ? c.text.slice(0, 48) + "…" : c.text}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Right: action buttons */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                  <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => togglePublish(note)}>
                    {note.published ? "Unpublish" : "Publish"}
                  </button>
                  <button className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => openEdit(note)}>
                    Edit
                  </button>
                  <button className="adm-btn adm-btn--quiet-danger adm-btn--sm" onClick={() => setConfirmDelete(note.version)}>
                    Archive
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ReleaseNotesUpdate;
